import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "@/context/CurrencyContext";
import { Globe, Coins, ChevronDown, Check } from "lucide-react";

// v2.0.11 — Switcher combiné langue + devise dans le header
// Phase 81 — langues et devises chargées depuis la configuration admin (/api/i18n/config)

export function LanguageCurrencySwitcher({ compact = false }) {
  const { i18n, t } = useTranslation();
  const { currency, setCurrency, currencies, languages } = useCurrency();
  const [openLang, setOpenLang] = useState(false);
  const [openCur, setOpenCur] = useState(false);

  const currentCode = (i18n.resolvedLanguage || "fr").split("-")[0];
  const current = languages.find((l) => l.code === currentCode) || languages[0];

  return (
    <div className="flex items-center gap-1">
      {/* Language */}
      <div className="relative">
        <button
          type="button"
          onClick={() => { setOpenLang(!openLang); setOpenCur(false); }}
          data-testid="lang-switcher-btn"
          className="inline-flex items-center gap-1.5 px-2 py-1.5 text-xs font-semibold border border-[#DEE2E6] hover:border-[#0A0A0A] hover:bg-[#F8F9FA] transition-colors"
          aria-label={t("common.language")}
        >
          <span className="text-base leading-none">{current.flag}</span>
          {!compact && <span className="uppercase">{current.code}</span>}
          <ChevronDown className="h-3 w-3" />
        </button>
        {openLang && (
          <div className="absolute right-0 mt-1 w-44 max-h-80 overflow-y-auto bg-white border border-[#0A0A0A] shadow-lg z-50" data-testid="lang-switcher-menu">
            {languages.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => { i18n.changeLanguage(l.code); setOpenLang(false); }}
                data-testid={`lang-opt-${l.code}`}
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-[#F1F3F5] ${l.code === currentCode ? "bg-[#F8F9FA] font-bold" : ""}`}
              >
                <span className="text-base leading-none">{l.flag}</span>
                <span className="flex-1">{l.label}</span>
                {l.code === currentCode && <Check className="h-3 w-3" />}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Currency */}
      <div className="relative">
        <button
          type="button"
          onClick={() => { setOpenCur(!openCur); setOpenLang(false); }}
          data-testid="cur-switcher-btn"
          className="inline-flex items-center gap-1.5 px-2 py-1.5 text-xs font-semibold border border-[#DEE2E6] hover:border-[#0A0A0A] hover:bg-[#F8F9FA] transition-colors"
          aria-label={t("common.currency")}
        >
          <Coins className="h-3.5 w-3.5" />
          {!compact && <span>{currency}</span>}
          <ChevronDown className="h-3 w-3" />
        </button>
        {openCur && (
          <div className="absolute right-0 mt-1 w-52 max-h-80 overflow-y-auto bg-white border border-[#0A0A0A] shadow-lg z-50" data-testid="cur-switcher-menu">
            {currencies.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => { setCurrency(c.code); setOpenCur(false); }}
                data-testid={`cur-opt-${c.code}`}
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-[#F1F3F5] ${c.code === currency ? "bg-[#F8F9FA] font-bold" : ""}`}
              >
                <span className="font-mono text-sm w-6">{c.symbol}</span>
                <span className="flex-1">{c.label}</span>
                <span className="text-[10px] text-[#868E96]">{c.code}</span>
                {c.code === currency && <Check className="h-3 w-3" />}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default LanguageCurrencySwitcher;
